import { groupApes, shortAddr, ledPositions, ledSlots, slotLeds, smooth, mixColor, readColor, backgroundColor, frameAt } from './monolith-pattern.js';

// Monolith registry and interactive light preview.
let threeReady;
class MonolithPage extends HTMLElement {
  #cleanup = [];
  #generation = 0;

  async connectedCallback() {
    const generation = ++this.#generation;
    if (!window.THREE) {
      threeReady ??= new Promise(resolve => {
        const script = document.createElement('script');
        script.src = this.dataset.threeUrl;
        script.onload = resolve;
        script.onerror = resolve; // The supplied flat preview works without WebGL.
        document.head.append(script);
      });
      await threeReady;
    }
    if (!this.isConnected || generation !== this.#generation) return;
    this.#initialize();
  }

  disconnectedCallback() {
    this.#generation++;
    this.#cleanup.splice(0).reverse().forEach(dispose => dispose());
  }

  #initialize() {
    const root = this;
    const generation = this.#generation;
    const copy = root.dataset;
    const cleanup = this.#cleanup;
    function listen(target, type, handler) {
      target.addEventListener(type, handler);
      cleanup.push(() => target.removeEventListener(type, handler));
    }
    // The slab: 5 wide, 3 deep, 10 tall. 15 columns of 10 modules = 150.
    var W = 5, D = 3, H = 10, COLS = W * D, TOTAL = COLS * H;
    var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    async function loadRegistry() {
      const controller = new AbortController();
      const cancel = () => controller.abort();
      cleanup.push(cancel);
      const timeout = setTimeout(cancel, 10000);
      try {
        const url = new URL(copy.registryUrl);
        if (url.protocol !== "https:") throw new Error("Registry requires HTTPS");
        const response = await fetch(url, { credentials: "omit", signal: controller.signal });
        if (!response.ok) throw new Error("Registry HTTP " + response.status);
        const payload = await response.json();
        if (payload.code !== 0 || !Array.isArray(payload.data)) throw new Error("Invalid registry response");
        const ids = new Set();
        const hex = /^#[0-9a-f]{6}$/i;
        return payload.data.map(function (ape) {
          // Temporary test default until the API supplies color_screen.
          const color = ape?.color_screen;
          const screenColor = color == null || (typeof color === "string" && !color.trim()) ? "#808080" : color;
          if (!ape || typeof ape.id !== "string" || !ape.id || ids.has(ape.id) ||
              typeof ape.model !== "string" || !ape.model || typeof ape.dna !== "string" || !ape.dna ||
              !hex.test(screenColor) || !Array.isArray(ape.patterns) || !ape.patterns.length ||
              !ape.patterns.every(p => p && typeof p.leds === "string" && /^[01]{150}$/.test(p.leds) &&
                hex.test(p.block) && Number.isFinite(p.delay) && p.delay > 0 && p.delay <= 2147483647) ||
              !Array.isArray(ape.nodes) || !ape.nodes.every(n => n && typeof n === "object")) {
            throw new Error("Invalid registry record (including color_screen or patterns)");
          }
          ids.add(ape.id);
          return { ...ape, color_screen: screenColor, name: ape.model + " #" + ape.dna };
        });
      } finally {
        clearTimeout(timeout);
        const index = cleanup.indexOf(cancel);
        if (index !== -1) cleanup.splice(index, 1);
      }
    }

    /* ======================================================================
       SLAB — a 3D preview where WebGL is available, the flat 15 × 10 grid
       where it is not. Plays a pattern set list out and back.
       ====================================================================== */
    function roundedBoxGeometry(size, radius, segments) {
      const geometry = new THREE.BoxGeometry(size, size, size, segments, segments, segments);
      const positions = geometry.attributes.position, inner = size / 2 - radius;
      const point = new THREE.Vector3(), center = new THREE.Vector3();
      for (let i = 0; i < positions.count; i++) {
        point.fromBufferAttribute(positions, i);
        center.copy(point).clampScalar(-inner, inner);
        point.sub(center).normalize().multiplyScalar(radius).add(center);
        positions.setXYZ(i, point.x, point.y, point.z);
      }
      geometry.computeVertexNormals();
      return geometry;
    }

    function makeSlab(stage, flat, onFrame) {
      // The slab turns on its own, but it yields the moment anyone handles it:
      // a drag, or a tap on a totem below, holds it still until five quiet
      // seconds have passed. Every fresh touch starts those five over.
      var HOLD_MS = 5000;
      var holdUntil = 0;
      function hold() { holdUntil = performance.now() + HOLD_MS; }

      var flatCells = [];
      for (var row = H - 1; row >= 0; row--) {
        for (var column = 0; column < COLS; column++) {
          var cell = document.createElement("i");
          flat.appendChild(cell);
          flatCells[row * COLS + column] = cell;
        }
      }

      var gl = null;
      if (window.THREE) {
        try {
          var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
          if (renderer && renderer.getContext()) {
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
            stage.insertBefore(renderer.domElement, stage.firstChild);
            flat.hidden = true;

            var scene = new THREE.Scene();
            var camera = new THREE.PerspectiveCamera(30, 1, 0.1, 200);
            var group = new THREE.Group();
            scene.add(group);

            var step = 1.14;
            var geo = roundedBoxGeometry(0.94, 0.2, 12);
            var outline = roundedBoxGeometry(0.94, 0.2, 2);
            var edgeGeo = new THREE.EdgesGeometry(outline, 20);
            outline.dispose();
            var edgeMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.15 });
            var mats = [];
            for (var r = 0; r < H; r++) {
              for (var dd = 0; dd < D; dd++) {
                for (var c = 0; c < W; c++) {
                  var mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.045 });
                  var mesh = new THREE.Mesh(geo, mat);
                  mesh.position.set((c - (W - 1) / 2) * step, (r - (H - 1) / 2) * step, (dd - (D - 1) / 2) * step);
                  group.add(mesh);
                  var line = new THREE.LineSegments(edgeGeo, edgeMat);
                  line.position.copy(mesh.position);
                  group.add(line);
                  mats[r * COLS + dd * W + c] = mat;
                }
              }
            }
            camera.position.set(13.8, 3.6, 19.6);
            camera.lookAt(0, 0, 0);
            gl = { renderer: renderer, scene: scene, camera: camera, group: group, mats: mats };
          }
        } catch (e) { gl = null; }
      }

      function resize() {
        if (!gl) return;
        var w = stage.clientWidth, h = stage.clientHeight;
        if (!w || !h) return;
        gl.renderer.setSize(w, h, false);
        gl.camera.aspect = w / h;
        gl.camera.updateProjectionMatrix();
        if (reduced) gl.renderer.render(gl.scene, gl.camera);
      }

      var frameId = null;
      function frame(now) {
        frameId = null;
        tick(now);
        if (gl) {
          if (!dragging && !reduced && now >= holdUntil) gl.group.rotation.y += 0.0035;
          gl.renderer.render(gl.scene, gl.camera);
        }
        if (!reduced) frameId = requestAnimationFrame(frame);
      }

      if (gl) {
        resize();
        if (window.ResizeObserver) { var observer = new ResizeObserver(resize); observer.observe(stage); cleanup.push(() => observer.disconnect()); }
        else listen(window, "resize", resize);

        var dragging = false, lastX = 0, lastY = 0;
        listen(stage, "pointerdown", function (e) {
          dragging = true; lastX = e.clientX; lastY = e.clientY;
          hold();
          if (stage.setPointerCapture) stage.setPointerCapture(e.pointerId);
        });
        listen(stage, "pointermove", function (e) {
          if (!dragging) return;
          hold();
          gl.group.rotation.y += (e.clientX - lastX) * 0.01;
          gl.group.rotation.x = Math.max(-0.6, Math.min(0.6, gl.group.rotation.x + (e.clientY - lastY) * 0.006));
          lastX = e.clientX; lastY = e.clientY;
          if (reduced) gl.renderer.render(gl.scene, gl.camera);
        });
        ["pointerup", "pointercancel", "pointerleave"].forEach(function (ev) {
          listen(stage, ev, function () {
            if (dragging) hold();
            dragging = false;
          });
        });
      }

      var resumeTimer = null;
      cleanup.push(() => {
        clearTimeout(resumeTimer);
        cancelAnimationFrame(frameId);
        stage.style.removeProperty("background");
        if (gl) {
          geo.dispose(); edgeGeo.dispose(); edgeMat.dispose();
          mats.forEach(material => material.dispose());
          gl.renderer.dispose();
          gl.renderer.domElement.remove();
        }
        flat.replaceChildren();
        flat.hidden = false;
      });
      var sets = [], idx = 0, pinnedIdx = -1, startMs = 0, lastStep = -1;
      var colors = [], lastColors = [], lastLevels = [], fromColors = null, fromLevels = null, switchedAt = 0;
      var ripples = [], nextRipple = 0, glowKey = "";

      function rippleLevel(now, i) {
        if (reduced) return 0.05;
        const position = ledPositions[i];
        let height = 0;
        ripples.forEach(wave => {
          const age = now - wave.start;
          const distance = Math.hypot(...position.map((value, axis) => value - wave.position[axis]));
          const offset = distance - age / 1000 * (2.5 / 3);
          height += Math.exp(-offset * offset / (2 * 1.4 * 1.4)) * smooth(age / 4500) *
            (1 - smooth((age - 4500) / 9000));
        });
        const brightness = Math.sqrt(0.05) + (1 - Math.sqrt(0.05)) * Math.min(1, height);
        return brightness * brightness;
      }

      function draw(state, now) {
        const current = sets[state.index], next = sets[state.next];
        const tint = colors[state.index].block, nextTint = colors[state.next].block;
        const bg = mixColor(colors[state.index].bg, colors[state.next].bg, state.bgMix);
        const peak = Math.max(...bg), gain = peak > 2 ? Math.min(170, 60 + peak * 1.2) / peak : 0;
        const key = bg.map(value => Math.round(value * gain)).join(',');
        if (key !== glowKey) {
          glowKey = key;
          stage.style.background = `radial-gradient(52% 40% at 50% 45%,rgba(${key},.38) 0%,rgba(${key},.14) 46%,rgba(${key},0) 100%)`;
        }
        const transition = reduced ? 1 : smooth((now - switchedAt) / 320);
        for (let i = 0; i < TOTAL; i++) {
          const on = current.leds[i] === '1', nextOn = next.leds[i] === '1';
          const level = rippleLevel(now, i);
          let color = on ? tint : backgroundColor(bg, tint, level);
          let lit = on ? 1 : 0;
          if (state.mix > 0) {
            color = mixColor(color, nextOn ? nextTint : backgroundColor(bg, nextTint, level), state.mix);
            lit += ((nextOn ? 1 : 0) - lit) * state.mix;
          }
          if (fromColors && transition < 1) {
            color = mixColor(fromColors[i], color, transition);
            lit = fromLevels[i] + (lit - fromLevels[i]) * transition;
          }
          lastColors[i] = color;
          lastLevels[i] = lit;
          const slot = ledSlots[i];
          if (gl) {
            gl.mats[slot].color.setRGB(...color.map(value => value / 255));
            gl.mats[slot].opacity = 0.42 + 0.54 * lit;
          }
          flatCells[slot].style.background = `rgb(${color.map(Math.round).join(',')})`;
        }
        if (transition === 1) { fromColors = null; fromLevels = null; }
      }

      function tick(now) {
        if (!sets.length) return;
        if (!reduced) {
          ripples = ripples.filter(wave => now - wave.start < 13500);
          if (now >= nextRipple) {
            nextRipple = now + 4500 + Math.random() * 4500;
            if (ripples.length < 5) ripples.push({ position: ledPositions[Math.floor(Math.random() * TOTAL)], start: now });
          }
        }
        const fixed = pinnedIdx >= 0 ? pinnedIdx : 0;
        const state = pinnedIdx >= 0 || reduced ? { index: fixed, next: fixed, mix: 0, bgMix: 0 } : frameAt(sets, now - startMs);
        draw(state, now);
        idx = state.index;
        if (idx !== lastStep) { lastStep = idx; if (onFrame) onFrame(idx, sets.length); }
      }

      // Picking a set out of the filmstrip parks the slab on it. Playback picks
      // up again on the same five quiet seconds the turn waits for, so a drag
      // in between keeps the chosen set on the board.
      function waitForQuiet() {
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(function () {
          if (performance.now() < holdUntil) { waitForQuiet(); return; }
          resumeTimer = null;
          pinnedIdx = -1;
          startMs = performance.now() - sets.slice(0, idx).reduce((total, pattern) => total + pattern.delay, 0);
        }, Math.max(holdUntil - performance.now(), 0) + 20);
      }

      return {
        has3d: !!gl,
        resize: resize,
        hold: hold,
        stop: function () {
          cancelAnimationFrame(frameId); frameId = null;
          clearTimeout(resumeTimer); resumeTimer = null;
          pinnedIdx = -1; lastStep = -1;
        },
        pick: function (k) {
          pinnedIdx = k;
          lastStep = -1;
          hold();
          if (reduced) frame(performance.now());
          else waitForQuiet();
        },
        play: function (ape) {
          const now = performance.now();
          if (sets !== ape.patterns) {
            fromColors = lastColors.length && !reduced ? lastColors.slice() : null;
            fromLevels = fromColors ? lastLevels.slice() : null;
            switchedAt = now;
            clearTimeout(resumeTimer); resumeTimer = null;
            sets = ape.patterns;
            colors = sets.map(pattern => ({ block: readColor(pattern.block), bg: readColor(pattern.bg) }));
            startMs = now; idx = 0; pinnedIdx = -1; lastStep = -1;
          }
          if (frameId === null) frame(now);
        }
      };
    }

    /* ======================================================================
       PAGE
       ====================================================================== */
    var stepsEl = root.querySelector('[data-monolith-id="steps"]');
    function markStep(i, n) {
      if (stepsEl.children.length !== n) {
        stepsEl.replaceChildren();
        for (var k = 0; k < n; k++) stepsEl.appendChild(document.createElement("i"));
      }
      Array.from(stepsEl.children).forEach(function (dot, k) { dot.classList.toggle("at", k === i); });
      stepsEl.setAttribute("aria-label", "Pattern " + (i + 1) + " of " + n);
    }

    var stage = root.querySelector('[data-monolith-id="stage"]');
    var mainSlab = makeSlab(stage, root.querySelector('[data-monolith-id="flat"]'), markStep);
    if (!mainSlab.has3d) root.querySelector('[data-monolith-id="hint"]').textContent = copy.staticLabel;

    var tents = root.querySelector('[data-monolith-id="tents"]');
    var tip = root.querySelector('[data-monolith-id="tip"]');
    var field = tents.parentElement;
    var showingEl = root.querySelector('[data-monolith-id="showing"]');
    var seriesEl = root.querySelector('[data-monolith-id="series"]');
    var colorEl = root.querySelector('[data-monolith-id="color"]');

    var sheet = root.querySelector('[data-monolith-id="sheet"]');
    var sheetSlab = null, sheetFrames = [];
    var APES = [], pinned = null;

    function tentSvg(color) {
      return '<svg viewBox="0 0 48 46" aria-hidden="true">' +
        '<path class="body" d="M24 7 45 39q-21 4.5-42 0Z" fill="' + color + '" stroke="rgba(255,255,255,.28)" stroke-width="1.2" stroke-linejoin="round"/>' +
        '<path d="M24 7 18 39.5q-5-.4-9.5-1.1Z" fill="rgba(0,0,0,.18)"/>' +
        '<path d="M24 16 31 40.3q-7 .8-14 0Z" fill="rgba(8,12,15,.82)"/>' +
        '<path d="M19.5 3.5 28.5 11M28.5 3.5 19.5 11" stroke="rgba(255,255,255,.45)" stroke-width="1.3" stroke-linecap="round"/>' +
        '</svg>';
    }

    function render(ape, pin) {
      mainSlab.play(ape);
      showingEl.textContent = ape.dna;
      seriesEl.textContent = ape.vibe;
      colorEl.textContent = ape.color;
      if (pin) {
        pinned = ape.id;
        APES.forEach(function (a) { a.el.setAttribute("aria-current", a.id === ape.id ? "true" : "false"); });
      }
    }

    function showTip(ape, el) {
      tip.innerHTML = "";
      var b = document.createElement("b");
      tip.style.setProperty('--tip-accent', ape.color_screen);
      b.textContent = ape.dna;
      tip.appendChild(b);
      if (ape.script) {
        const script = document.createElement('p');
        script.className = 'tip-script';
        script.textContent = ape.script;
        tip.appendChild(script);
      }
      [[copy.checkedInLabel, ape.registeredAt]].forEach(function (row) {
        var s = document.createElement("span");
        var k = document.createElement("em");
        k.style.fontStyle = "normal";
        k.textContent = row[0];
        var val = document.createElement("i");
        val.textContent = row[1];
        s.appendChild(k); s.appendChild(val);
        tip.appendChild(s);
      });
      tip.hidden = false;

      var fr = field.getBoundingClientRect(), tr = el.getBoundingClientRect();
      var left = tr.left - fr.left + tr.width / 2 - tip.offsetWidth / 2;
      left = Math.max(0, Math.min(left, fr.width - tip.offsetWidth));
      var top = tr.top - fr.top - tip.offsetHeight - 8;
      if (top < 0) top = tr.top - fr.top + tr.height + 8;
      tip.style.left = left + "px";
      tip.style.top = top + "px";
    }

    function hideTip() {
      tip.hidden = true;
      var ape = APES.find(a => a.id === pinned);
      if (ape) render(ape, false);
    }

    function openSheet(ape) {
      if (!sheetSlab) {
        sheetSlab = makeSlab(root.querySelector('[data-monolith-id="sheetStage"]'), root.querySelector('[data-monolith-id="sheetFlat"]'),
          function (i) {
            sheetFrames.forEach(function (f, k) { f.classList.toggle("at", k === i); });
          });
      }
      root.querySelector('[data-monolith-id="sheetVibe"]').textContent = /^(3x|original)$/i.test(ape.model) ? 'Nervape ' + ape.model : ape.model;
      root.querySelector('[data-monolith-id="sheetName"]').textContent = ape.dna;
      root.querySelector('[data-monolith-id="sheetScript"]').textContent = ape.script || '—';
      root.querySelector('[data-monolith-id="sheetSeries"]').textContent = ape.vibe || '—';
      root.querySelector('[data-monolith-id="sheetColor"]').textContent = ape.color || '—';
      root.querySelector('[data-monolith-id="sheetOwner"]').textContent = shortAddr(ape.owner);

      // Filmstrip: every set is the whole slab — all three 5 × 10 faces,
      // front to back — with the time that set holds.
      var strip = root.querySelector('[data-monolith-id="sheetStrip"]');
      strip.innerHTML = "";
      sheetFrames = [];
      ape.patterns.forEach(function (p, k) {
        var fig = document.createElement("button");
        fig.type = "button";
        fig.className = "frame";
        fig.setAttribute("aria-label", "Pattern set " + (k + 1) + " of " + ape.patterns.length + ", holds " + p.delay + "ms");
        fig.addEventListener("click", function () { sheetSlab.pick(k); });
        var faces = document.createElement("div");
        faces.className = "faces";
        for (var d = 0; d < D; d++) {
          var grid = document.createElement("div");
          grid.className = "grid";
          for (var r = H - 1; r >= 0; r--) {
            for (var c = 0; c < W; c++) {
              var cell = document.createElement("i");
              cell.style.background = p.leds.charCodeAt(slotLeds[r * COLS + d * W + c]) === 49 ? p.block : `rgb(${backgroundColor(readColor(p.bg), readColor(p.block), 0.05).map(Math.round).join(',')})`;
              grid.appendChild(cell);
            }
          }
          faces.appendChild(grid);
        }
        var ms = document.createElement("small");
        ms.textContent = p.delay + "ms";
        fig.appendChild(faces);
        fig.appendChild(ms);
        strip.appendChild(fig);
        sheetFrames.push(fig);
      });

      var list = root.querySelector('[data-monolith-id="sheetNodes"]');
      list.innerHTML = "";
      ape.nodes.forEach(function (n, i) {
        var li = document.createElement("li");
        var no = document.createElement("span");
        no.className = "no";
        no.textContent = String(i + 1).padStart(2, "0");
        var where = document.createElement("span");
        where.className = "where";
        var nb = document.createElement("b");
        nb.textContent = n.name;
        var np = document.createElement("span");
        np.textContent = n.place;
        var no2 = document.createElement("span");
        no2.className = "who";
        var tag = document.createElement("em");
        tag.textContent = copy.ownerThenLabel + " · ";
        no2.appendChild(tag);
        no2.append(shortAddr(n.owner));
        where.appendChild(nb); where.appendChild(np); where.appendChild(no2);
        var when = document.createElement("span");
        when.className = "when";
        when.textContent = n.date;
        li.appendChild(no); li.appendChild(where); li.appendChild(when);
        list.appendChild(li);
      });

      if (typeof sheet.showModal === "function") sheet.showModal();
      else sheet.setAttribute("open", "");
      sheetSlab.play(ape);
      sheetSlab.resize();
    }

    function closeSheet() {
      if (sheetSlab) sheetSlab.stop();
      if (typeof sheet.close === "function") sheet.close();
      else sheet.removeAttribute("open");
    }
    listen(root.querySelector('[data-monolith-id="sheetClose"]'), "click", closeSheet);
    listen(sheet, "click", function (event) { if (event.target === sheet) closeSheet(); });
    listen(sheet, "close", function () { if (sheetSlab) sheetSlab.stop(); });

    cleanup.push(() => { sheet.close(); });

    /* ---------- how to check in ---------- */
    var howto = root.querySelector('[data-monolith-id="howto"]');
    listen(root.querySelector('[data-monolith-id="howtoBtn"]'), "click", function () {
      if (typeof howto.showModal === "function") howto.showModal();
      else howto.setAttribute("open", "");
    });
    listen(root.querySelector('[data-monolith-id="howtoClose"]'), "click", function () {
      if (typeof howto.close === "function") howto.close();
      else howto.removeAttribute("open");
    });
    listen(howto, "click", function (e) { if (e.target === howto) howto.close(); });

    cleanup.push(() => howto.close());

    /* ---------- phone: the camp lives in a drawer beside the slab ---------- */
    var phone = window.matchMedia("(max-width: 900px)");
    var campCol = root.querySelector('[data-monolith-id="campCol"]');
    var drawerBtn = root.querySelector('[data-monolith-id="drawerBtn"]');
    var scrim = root.querySelector('[data-monolith-id="scrim"]');

    var drawerOverflow;
    function openDrawer() {
      drawerOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      campCol.setAttribute('role', 'dialog');
      campCol.setAttribute('aria-modal', 'true');
      campCol.setAttribute('aria-label', copy.drawerLabel);
      campCol.classList.add("open");
      root.classList.add("drawer-open");
      scrim.hidden = false;
      drawerBtn.setAttribute("aria-expanded", "true");
      campCol.querySelector("button:not([hidden])")?.focus({ preventScroll: true });
    }
    function closeDrawer() {
      const wasOpen = campCol.classList.contains('open');
      if (wasOpen) document.documentElement.style.overflow = drawerOverflow;
      campCol.removeAttribute('role');
      campCol.removeAttribute('aria-modal');
      campCol.classList.remove("open");
      root.classList.remove("drawer-open");
      scrim.hidden = true;
      drawerBtn.setAttribute("aria-expanded", "false");
      if (wasOpen && root.isConnected) drawerBtn.focus({ preventScroll: true });
    }
    listen(drawerBtn, "click", function () {
      if (campCol.classList.contains("open")) closeDrawer(); else openDrawer();
    });
    listen(scrim, "click", closeDrawer);
    cleanup.push(closeDrawer);
    listen(document, 'keydown', event => {
      if (event.key !== 'Tab' || !campCol.classList.contains('open')) return;
      const buttons = [...campCol.querySelectorAll('button:not([hidden])'), drawerBtn];
      const first = buttons[0], last = buttons.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    listen(document, "keydown", function (e) {
      if (e.key === "Escape" && campCol.classList.contains("open")) closeDrawer();
    });
    listen(phone, "change", function () { if (!phone.matches) closeDrawer(); });

    listen(root.querySelector('[data-monolith-id="recordBtn"]'), "click", function () {
      const ape = APES.find(a => a.id === pinned);
      if (ape) openSheet(ape);
    });

    // The drawer handle only belongs on screen while the node section is.
    var wall = root.querySelector(".wall");
    if (window.IntersectionObserver) {
      var wallObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          root.classList.toggle("at-node", en.isIntersecting);
          if (!en.isIntersecting) closeDrawer();
        });
      }, { threshold: 0.15 });
      wallObserver.observe(wall);
      cleanup.push(() => wallObserver.disconnect());
    } else {
      root.classList.add("at-node");
    }

    const countEl = root.querySelector('[data-monolith-id="count"]');
    const recordBtn = root.querySelector('[data-monolith-id="recordBtn"]');
    const retryBtn = root.querySelector('[data-monolith-id="retryBtn"]');
    async function refreshRegistry() {
      tents.replaceChildren();
      APES = [];
      pinned = null;
      tip.hidden = true;
      showingEl.textContent = seriesEl.textContent = colorEl.textContent = "—";
      stepsEl.replaceChildren();
      stepsEl.removeAttribute("aria-label");
      recordBtn.disabled = true;
      retryBtn.hidden = true;
      countEl.textContent = copy.loadingLabel;
      try {
        const data = await loadRegistry();
        if (!root.isConnected || generation !== root.#generation) return;
        APES = groupApes(data);
        root.querySelector('[data-monolith-id="count"]').textContent = copy.countTemplate.replaceAll("[count]", APES.length);

        APES.forEach(function (ape) {
          var t = document.createElement("button");
          t.type = "button";
          t.className = "tent";
          t.setAttribute("aria-label", ape.name + ", " + ape.vibe + ", " + ape.patterns.length + " pattern sets, registered " + ape.registeredAt);
          t.setAttribute("aria-current", "false");
          t.innerHTML = '<span class="ground"></span>' + tentSvg(ape.color_screen);
          t.addEventListener("pointerenter", function () { showTip(ape, t); render(ape, false); });
          t.addEventListener("focus", function () { showTip(ape, t); render(ape, false); });
          t.addEventListener("pointerleave", hideTip);
          t.addEventListener("blur", hideTip);
          t.addEventListener("click", function () {
            mainSlab.hold();
            render(ape, true);
            // On a phone the drawer covers the slab, so a tap shows the totem
            // and gets out of the way; the record is one more tap from there.
            if (phone.matches) { tip.hidden = true; closeDrawer(); }
            else openSheet(ape);
          });
          tents.appendChild(t);
          ape.el = t;
        });

        if (APES.length) {
          render(APES[0], true);
          recordBtn.disabled = false;
        } else {
          countEl.textContent = copy.emptyLabel;
        }
      } catch (error) {
        if (!root.isConnected || generation !== root.#generation) return;
        countEl.textContent = copy.errorLabel;
        retryBtn.hidden = false;
        console.warn("Monolith registry:", error.message);
      }
    }
    listen(retryBtn, "click", refreshRegistry);
    refreshRegistry();

  }
}
if (!customElements.get('monolith-page')) customElements.define('monolith-page', MonolithPage);
